/*
Purpose: Centralized modal layering and z-index management
Audience: All modal components that need proper layering
What it does:
- Manages z-index layers for nested modals
- Provides portal containers for different modal types
- Ensures proper stacking order
Interplay:
- Used by Evidence, Results, and Bounty modals for proper layering
*/

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

// Z-index constants for different modal layers
export const Z_INDEX_LAYERS = {
  BASE_MODAL: 9999,        // Base modals (Evidence, etc.)
  OVERLAY_MODAL: 15000,    // Modals that appear over base modals (Results, Bounty)
  NESTED_MODAL: 20000,     // Nested modals (Full Details within Results)
  VEHICLE_PANEL: 22000,    // Vehicle Details Panel
  OVERLAY_STUDIO: 25000,   // Overlay Studio (highest priority)
  TOOLTIP: 30000,          // Tooltips and small overlays
  CRITICAL: 35000          // Critical alerts that must be on top
};

// Modal Manager Context
const ModalManagerContext = createContext();

// Modal Manager Provider
export const ModalManagerProvider = ({ children }) => {
  const [activeModals, setActiveModals] = useState(new Set());
  const [modalStack, setModalStack] = useState([]);

  const registerModal = useCallback((modalId, layer = Z_INDEX_LAYERS.BASE_MODAL) => {
    setActiveModals(prev => new Set([...prev, modalId]));
    setModalStack(prev => [...prev, { id: modalId, layer, timestamp: Date.now() }]);
  }, []);

  const unregisterModal = useCallback((modalId) => {
    setActiveModals(prev => {
      const newSet = new Set(prev);
      newSet.delete(modalId);
      return newSet;
    });
    setModalStack(prev => prev.filter(modal => modal.id !== modalId));
  }, []);

  const getModalZIndex = useCallback((modalId) => {
    const modal = modalStack.find(m => m.id === modalId);
    if (!modal) return Z_INDEX_LAYERS.BASE_MODAL;
    
    // Calculate z-index based on position in stack
    const stackPosition = modalStack.indexOf(modal);
    return modal.layer + stackPosition;
  }, [modalStack]);

  const contextValue = useMemo(() => ({
    activeModals,
    modalStack,
    registerModal,
    unregisterModal,
    getModalZIndex,
    Z_INDEX_LAYERS
  }), [activeModals, modalStack, registerModal, unregisterModal, getModalZIndex]);

  return (
    <ModalManagerContext.Provider value={contextValue}>
      {children}
    </ModalManagerContext.Provider>
  );
};

// Hook to use modal manager
export const useModalManager = () => {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error('useModalManager must be used within a ModalManagerProvider');
  }
  return context;
};

// Enhanced Modal Portal with proper layering
export const LayeredModalPortal = ({ 
  children, 
  modalId, 
  layer = Z_INDEX_LAYERS.BASE_MODAL,
  parentModalId = null 
}) => {
  const { registerModal, unregisterModal, getModalZIndex } = useModalManager();

  useEffect(() => {
    // Calculate layer based on parent modal
    const effectiveLayer = parentModalId ? Z_INDEX_LAYERS.OVERLAY_MODAL : layer;
    registerModal(modalId, effectiveLayer);
    
    return () => {
      unregisterModal(modalId);
    };
  }, [modalId, layer, parentModalId, registerModal, unregisterModal]);

  const zIndex = getModalZIndex(modalId);

  // Create a wrapper div with the calculated z-index
  const modalContent = (
    <div 
      style={{ 
        zIndex,
        position: 'relative',
        isolation: 'isolate' // Create new stacking context
      }}
      data-modal-id={modalId}
      data-modal-layer={layer}
    >
      {children}
    </div>
  );

  return createPortal(modalContent, document.body);
};

// Convenience component for overlay modals (Results, Bounty, etc.)
export const OverlayModalPortal = ({ children, modalId, parentModalId }) => (
  <LayeredModalPortal
    modalId={modalId}
    layer={Z_INDEX_LAYERS.OVERLAY_MODAL}
    parentModalId={parentModalId}
  >
    {children}
  </LayeredModalPortal>
);

export default ModalManagerProvider;
